import { Component, OnInit } from '@angular/core';
import { KidsService } from '../../../services/kids.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { TransactionsService } from 'src/app/services/transactions.service';
import { AuthService } from 'src/app/services/auth.service';
import { forkJoin } from 'rxjs';
import { TitreVisible } from 'src/app/models/kids';

@Component({
  selector: 'app-kids',
  templateUrl: './kids.component.html',
  styleUrls: ['../shared/tx-table.css', './kids.component.css'],
  providers: [MessageService, ConfirmationService]
})
export class KidsComponent implements OnInit {
  // --- Variables d'état ---
  search = '';
  statusFilter = 'ALL';
  showModal = false;
  showDetailsModal = false;
  showErrorModal = false;
  loading = false;

  // --- Données ---
  kids: any[] = [];
  allTickets: any[] = [];
  selectedKid: any = null;
  transactions: any[] = [];
  errorMessage: string = '';
  serverErrorMessage: string = '';
  merchantId: string = '';
  merchantWalletAddress: string = '';
  selectedKidTitres: TitreVisible[] = [];
  loadingTitres = false;

  newKid: any = {
    name: '',
    address: '',
    mode: '',
    responsibleName: '',
    email: '',
    phone: ''
  };

  constructor(
    private kidsService: KidsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private transactionsService: TransactionsService,
    private authService: AuthService,
  ) {}

  ngOnInit() {
    this.loadTickets();
    this.loadAllData();
  }

  get selectedKidTotal(): number {
    return this.selectedKidTitres.reduce((sum, t) => sum + t.solde, 0);
  }

  loadAllData() {
    this.authService.getUser().subscribe({
      next: (user) => {
        this.merchantId = user?.sub || '';
        this.merchantWalletAddress = user?.account || '';

        forkJoin({
          tickets: this.kidsService.getTickets(),
          kids: this.kidsService.getKids(),
          transactions: this.transactionsService.getTransactions('merchant', this.merchantId)
        }).subscribe({
          next: (data) => {
            this.allTickets = data.tickets.tickets || [];
            this.transactions = data.transactions || [];
            this.processKids(data.kids);
          },
          error: (err) => {
            console.error("Erreur chargement données", err);
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: 'Impossible de charger les données.'
            });
          }
        });
      },
      error: (err) => {
        console.error("Erreur chargement utilisateur", err);
      }
    });
  }

  /**
   * Solde par titre pour ce kid — calculé directement depuis les transactions déjà
   * chargées, sans appel réseau supplémentaire. Depuis le passage de "Bon cadeau" au
   * solde partagé façon B2B, tx.amount porte déjà le vrai montant pour tous les titres,
   * B2B comme "Bon cadeau" : plus de distinction ni de résolution de prix à part.
   */
  private loadTitresForKid(kid: any): void {
    this.selectedKidTitres = (kid.acceptedTickets || [])
      .map((id: any): TitreVisible => ({
        id,
        label: this.getTicketLabel(id),
        solde: this.calculateKidBalanceByTicket(kid.customerId, kid.displayName, id),
        isB2C: false,
      }))
      .filter((t: TitreVisible) => t.solde > 0);
  }

  processKids(kidsData: any[]) {
    this.kids = kidsData.map(k => {
      const rawDate = k.createdAt || k.createdDate || null;
      const kidName = k.posName || (k.name ? k.name.split(' - ')[1] : k.name);

      const solde = this.calculateKidBalance(k.customerId, kidName);

      return {
        ...k,
        displayName: kidName,
        mode: k.kidMode === 'LOGIN' ? 'Boutique' : 'Caisse',
        rawDate: rawDate,
        createdAt: rawDate ? new Date(rawDate).toLocaleDateString('fr-FR') : '---',
        totalCoupons: k.totalCoupons || 0,
        totalSales: k.totalSales || 0,
        solde: solde,
        lastActivity: k.lastActivity ? new Date(k.lastActivity).toLocaleString('fr-FR') : 'Aucune',
        acceptedTickets: k.acceptedTickets || []
      };
    }).sort((a, b) => {
      const dateB = b.rawDate ? new Date(b.rawDate).getTime() : 0;
      const dateA = a.rawDate ? new Date(a.rawDate).getTime() : 0;
      return dateB - dateA;
    });
  }

  calculateKidBalance(customerId: string, kidName: string): number {
    if (!this.transactions || this.transactions.length === 0) {
      return 0;
    }

    const kidTransactions = this.transactions.filter(tx => {
      const belongsToKid =
        tx.merchantKidId === customerId ||
        tx.merchantKidId === kidName;
      const isConfirmed = tx.status === 'Confirmé';
      return belongsToKid && isConfirmed;
    });

    return kidTransactions.reduce((sum, tx) => {
      const amount = parseFloat(tx.amount) || 0;
      return sum + amount;
    }, 0);
  }

  calculateKidBalanceByTicket(customerId: string, kidName: string, ticketId: number): number {
    if (!this.transactions || this.transactions.length === 0) {
      return 0;
    }

    return this.transactions
      .filter(tx =>
        (tx.merchantKidId === customerId || tx.merchantKidId === kidName) &&
        String(tx.ticketId) === String(ticketId) &&
        tx.status === 'Confirmé'
      )
      .reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);
  }

  loadTickets() {
    this.kidsService.getTickets().subscribe({
      next: (res: any) => {
        this.allTickets = res.tickets || [];
      },
      error: (err) => console.error("Erreur chargement labels tickets", err)
    });
  }

  getTicketLabel(ticketId: number): string {
    const ticket = this.allTickets.find(t => t.id === ticketId);
    return ticket ? ticket.label : `Ticket #${ticketId}`;
  }

  // --- Actions ---
  openModal() { this.showModal = true; }
  closeModal() { this.showModal = false; this.resetNewKid(); }

  openDetails(kid: any) {
    this.selectedKid = kid;
    this.showDetailsModal = true;
    this.loadTitresForKid(kid);
  }
  closeDetailsModal() {
    this.showDetailsModal = false;
    this.selectedKid = null;
  }

  resetNewKid() {
    this.errorMessage = '';
    this.newKid = { name: '', address: '', mode: '', responsibleName: '', email: '', phone: '' };
  }

  addKid() {
    if (!this.newKid.mode) {
      this.errorMessage = "Le type est obligatoire.";
      return;
    }
    this.loading = true;
    const payload = {
      posName: this.newKid.name || null,
      address: this.newKid.address || null,
      kidMode: this.newKid.mode === 'Boutique' ? 'LOGIN' : 'TECHNICAL',
      ownerFirstName: this.newKid.responsibleName || null,
      ownerEmail: this.newKid.email || null,
      ownerPhone: this.newKid.phone || null
    };

    this.kidsService.addKid(payload).subscribe({
      next: () => {
        this.loading = false;
        this.messageService.add({ severity: 'success', summary: 'Succès', detail: `Créé avec succès.` });
        this.loadAllData();
        this.closeModal();
      },
      error: (err) => {
        this.loading = false;
        this.closeModal();
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error?.message || 'Erreur lors de la création.'
        });
      }
    });
  }

  toggleStatus(kid: any) {
    const newStatus = !kid.active;
    this.kidsService.updateKidStatus(kid.customerId, newStatus).subscribe({
      next: () => {
        kid.active = newStatus;
        this.messageService.add({ severity: 'success', summary: 'Statut mis à jour' });
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: err.error?.message });
      }
    });
  }

  filteredKids() {
    return this.kids.filter(kid => {
      const matchesSearch = (kid.displayName || '').toLowerCase().includes(this.search.toLowerCase());
      const matchesStatus = this.statusFilter === 'ALL' ||
        (this.statusFilter === 'ACTIVE' && kid.active) ||
        (this.statusFilter === 'INACTIVE' && !kid.active);
      return matchesSearch && matchesStatus;
    });
  }
}